/**
 * Diese Komponente erlaubt das Bearbeiten von HelpPosts.
 */

import 'regenerator-runtime/runtime'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Box, Grid, Paper, Stack, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { EHelpPostType, GetHelpPostRequest, HelpPost } from '@professocialApi/professocial-core-frontend-api'
import { useHistory, useParams } from 'react-router-dom'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { DropDownElement } from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import DatePickerComponent from '@professocial/professocialFormComponents/FormComponents/DatePickerComponent'
import MediaUploadComponent from '@professocial/professocialFormComponents/FormComponents/MediaUploadComponent'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import VideoComponent from '@professocial/professocialFormComponents/FormComponents/VideoComponent'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { useDispatch, useSelector } from 'react-redux'
import { CoreRootState } from '../../../../store/coreRoot.reducer'
import {
  createEditorHelpPostInitiated, getHelpPostInitiated, setHelpPost,
  updateEditorHelpPostInitiated,
} from '../../../../store/help/help.actions'
import { namedRequestData } from '../../../../store/common/common.selectors'
import {
  checkFileUploadInitiated,
  getUploadedFileAsBase64Initiated,
  setUploadedFileId
} from '../../../../store/images/images.actions'
import { CheckFileUploadRequest } from '../../../../api/filescanner/types'
import { getUploadedFile, getUploadedFileId } from '../../../../store/images/images.selector'
import { GetUploadedFileAsBase64Request, UploadedFileStatus } from '../../../../api/filescanner/types'
import { helpPost } from '../../../../store/help/help.selector'

export interface Props {
  createEditorHelpPost?: (postData: HelpPost) => void
  updateEditorHelpPost?: (postData: HelpPost) => void
  saveSuccess?: boolean
}

type ICreateHelpPostForm = {
  entityId: string
  headline: string
  type: DropDownElement
  date: Date
  text: string
  linkUrl: string
  linkText: string
  uploadedImageId: string | undefined
  video: any | undefined
  videoUrl: string | undefined
  picture: string | undefined
}

export type InputAttr = {
  inputComponent: string
  id: 'headline' | 'type' | 'text' | 'linkUrl' | 'date' | 'linkText' | 'uploadedImageId' | 'videoUrl'
  label: string
  type?: string
  active?: boolean
  disabled?: boolean
  options?: any
  visibility?: boolean
  required?: boolean
  handleChange?: any
  autoComplete?: string
  additionalButton?: boolean
  additionalButtonIcon?: any
  additionalButtonText?: string
  additionalButtonOnClick?: () => void
  rows?: number
  multiline?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(3),
  },
  button: {
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(4),
  },
  imgBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  paper: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(3),
  },
}))

const EditorHelpPostEditor: React.FC<Props> = (props) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const history = useHistory()
  const { t } = useTranslation()
  const { helpPostId } = useParams<{ helpPostId: string }>()

  const helpPostData = useSelector(helpPost)
  const uploadedFileId = useSelector(getUploadedFileId)
  const uploadedFile = useSelector(getUploadedFile)

  const dispatchSetUploadedFileID = useCallback((request: string) => dispatch(setUploadedFileId(request)), [dispatch])
  const dispatchSetHelpPost = useCallback((request: HelpPost | undefined) => dispatch(setHelpPost(request)), [dispatch])
  const dispatchGetHelpPost = useCallback((request: GetHelpPostRequest) => dispatch(getHelpPostInitiated(request)), [dispatch])
  const dispatchCheckFileUpload = useCallback((request: CheckFileUploadRequest) => dispatch(checkFileUploadInitiated(request)), [dispatch])
  const dispatchGetUploadedFileAsBase64 = useCallback((request: GetUploadedFileAsBase64Request) => dispatch(getUploadedFileAsBase64Initiated(request)), [dispatch])

  const postTypes: DropDownElement[] = [
    {
      value: t('coreHelp:postTypeText'),
      key: EHelpPostType.Text,
    },
    {
      value: t('coreHelp:postTypeYTVideo'),
      key: EHelpPostType.YtVideo,
    },
    {
      value: t('coreHelp:postTypeVideo'),
      key: EHelpPostType.Video,
    },
  ]

  const defaultPost: HelpPost = {
    entityId: '',
    video: undefined,
    uploadedImageId: '',
    picture: '',
    headline: '',
    linkText: '',
    type: EHelpPostType.Text,
    date: new Date(Date.now()),
    linkUrl: '',
    videoUrl: '',
    text: '',
  }

  const [data, setData] = useState<HelpPost | undefined>()
  const [date, setDate] = useState<Date | undefined>(undefined)
  const [showImageLoader, setShowImageLoader] = useState<boolean>(false)
  const [postType, setPostType] = useState<EHelpPostType>()
  const [currentInterval, setCurrentInterval] = useState<ReturnType<typeof setInterval>>()

  const onCreateEditorHelpPost: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, createEditorHelpPostInitiated.type))
  const onUpdateEditorHelpPost: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, updateEditorHelpPostInitiated.type))
  const onCheckFileUpload: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, checkFileUploadInitiated.type))
  const onGetUploadedFileAsBase64: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, getUploadedFileAsBase64Initiated.type))

  const defaultValues: ICreateHelpPostForm = {
    entityId: '',
    headline: (data && data.headline) || '',
    text: (data && data.text) || '',
    type: (data && data.type && postTypes.find(item => item.key === data.type) as DropDownElement) || postTypes[0],
    date: (data && data.date) || new Date(Date.now()),
    linkText: (data && data.linkText) || '',
    linkUrl: (data && data.linkUrl) || '',
    uploadedImageId: (data && data.uploadedImageId) || '',
    video: (data && data.video) || undefined,
    videoUrl: (data && data.videoUrl) || undefined,
    picture: (data && data.picture) || undefined,
  }

  const validationSchema = Yup.object<Record<keyof ICreateHelpPostForm, Yup.AnySchema>>().shape({
    headline: Yup.string().required(`${t('coreHelp:missingInputError')}`),
    text: Yup.string().required(`${t('coreHelp:missingInputError')}`),
    type: Yup.object(),
    date: Yup.date(),
    linkText: Yup.string(),
    linkUrl: Yup.string(),
  })

  const {
    register,
    reset,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ICreateHelpPostForm>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const inputComponents: InputAttr[] = [
    {
      inputComponent: 'dropDown',
      id: 'type',
      label: `${t('coreHelp:postType')}`,
      options: postTypes,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'headline',
      label: `${t('coreHelp:postHeader')}`,
      disabled: false,
      visibility: true,
      required: true,
      autoComplete: 'off',
      active: true,
    },
    {
      inputComponent: 'date',
      id: 'date',
      label: `${t('coreHelp:postDate')}`,
      disabled: true,
      required: false,
      visibility: true,
      active: true,
    },
    {
      inputComponent: 'text',
      id: 'videoUrl',
      label: `${t('coreHelp:videoUrl')}`,
      disabled: false,
      visibility: true,
      active: true,
      autoComplete: 'off',
    },
    {
      inputComponent: 'text',
      id: 'text',
      label: `${t('coreHelp:text')}`,
      disabled: false,
      visibility: true,
      required: true,
      autoComplete: 'off',
      active: true,
      multiline: true,
      rows: 6,
    },
    {
      inputComponent: 'text',
      id: 'linkText',
      label: `${t('coreHelp:linkText')}`,
      disabled: false,
      visibility: true,
      active: true,
      autoComplete: 'off',
    },
    {
      inputComponent: 'text',
      id: 'linkUrl',
      label: `${t('coreHelp:linkUrl')}`,
      disabled: false,
      visibility: true,
      active: true,
      autoComplete: 'off',
    },
  ]

  const handleUpdateEditorHelpPost: SubmitHandler<ICreateHelpPostForm> = submitData => {
    if (submitData) {
      switch (postType) {
      case EHelpPostType.Text:
        submitData.videoUrl = undefined
        submitData.video = undefined
        break
      case EHelpPostType.Video:
        submitData.uploadedImageId = undefined
        break
      case EHelpPostType.YtVideo:
        submitData.uploadedImageId = undefined
        break
      }

      const params: HelpPost = {
        ...submitData,
        entityId: helpPostId || undefined,
        video: submitData.video || undefined,
        uploadedImageId: submitData.uploadedImageId === '' ? '' : submitData.uploadedImageId,
        picture: undefined,
        headline: submitData.headline,
        linkText: submitData.linkText,
        type: postType || EHelpPostType.Text,
        date: date,
        linkUrl: submitData.linkUrl || undefined,
        videoUrl: submitData.videoUrl || undefined,
        text: submitData.text || undefined,
      }

      if (helpPostId) {
        props.updateEditorHelpPost && props.updateEditorHelpPost(params)
      } else {
        props.createEditorHelpPost && props.createEditorHelpPost(params)
      }
    }
  }

  const scanFile = ({ file }: { file: Blob }) => {
    setShowImageLoader(true)
    dispatchCheckFileUpload({ file, filename: `image${Date.now()}` })
  }

  useEffect(() => {
    helpPostId && dispatchGetHelpPost({ postId: helpPostId })
  }, [helpPostId])

  useEffect(() => {
    if (helpPostData && helpPostId) {
      setData(helpPostData)
      setPostType(helpPostData.type)
      setDate(helpPostData.date)
    }
  }, [helpPostData, helpPostId])

  useEffect(() => { reset(defaultValues) }, [reset, data])

  useEffect(() => {
    if (uploadedFileId) {
      const interval = setInterval(() => dispatchGetUploadedFileAsBase64({ uploadedFileId }), 500)
      setCurrentInterval(interval)
    }

    return () => clearTimeout(currentInterval)
  }, [uploadedFileId])

  useEffect(() => {
    if (uploadedFile?.status === UploadedFileStatus.DELIVERED) {
      setValue('uploadedImageId', uploadedFileId, { shouldDirty: true })
      setShowImageLoader(false)
      clearTimeout(currentInterval)
    }
  }, [uploadedFile])

  useEffect(() => {
    if (onCheckFileUpload?.status === 'failed' || onGetUploadedFileAsBase64?.status === 'failed') setShowImageLoader(false)
  }, [onCheckFileUpload, onGetUploadedFileAsBase64])

  useEffect(() => {
    // Navigate back to help post list after successful creation or update
    if (onCreateEditorHelpPost?.status === 'success' || onUpdateEditorHelpPost?.status === 'success') {
      setData(defaultPost)
      dispatchSetUploadedFileID('')
      dispatchSetHelpPost(undefined)
      history.push('/editor/help')
    }
  }, [onCreateEditorHelpPost, onUpdateEditorHelpPost, history, defaultPost, dispatchSetUploadedFileID, dispatchSetHelpPost])

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <form autoComplete='off' style={{ width: '100%' }}>
          <Grid container spacing={2} sx={{ px: 2, pb: 6, mt: 1 }}>
            <Box className={classes.button}>
              <ButtonComponent onClick={() => {
                setData(defaultPost)
                dispatchSetUploadedFileID('')
                dispatchSetHelpPost(undefined)
                history.push('/editor/help')
              }} color='primary' variant='outlined' disableElevation text={t('coreCommon:backToPAge')} />
            </Box>
            <Grid container item xs={12} sm={12} md={12} lg={12} spacing={3}>
              <Grid item xs={12} md={5} >
                {inputComponents && inputComponents.length !== 0 && inputComponents.map((row, index: number) => (
                  <React.Fragment key={index}>
                    {row.active && row.visibility &&
                      <Box sx={{ width: '100%', pb: 2, display: (row.id === 'videoUrl' && getValues('type').key != EHelpPostType.YtVideo) ? 'none' : '' }}>
                        {row.inputComponent === 'text'
                          ? (
                            <TextFieldComponent
                              {...register(row.id)}
                              id={row.id}
                              label={row.label}
                              type={row.type}
                              size='small'
                              rows={row.rows}
                              autoComplete={row.autoComplete}
                              disabled={row.disabled}
                              required={row.required}
                              fullWidth
                              multiline={row.multiline}
                              error={!!errors[row.id]}
                              helperText={errors[row.id]?.message}
                            />
                          )
                          : (row.inputComponent === 'dropDown' ? (
                            <>
                              {row && row.options &&
                                <Controller
                                  control={control}
                                  name={row.id}
                                  render={({ field: { onChange, value } }) => (
                                    <Autocomplete
                                      id={row.id}
                                      value={value as DropDownElement || null}
                                      options={row.options}
                                      onChange={(event, item) => {
                                        onChange(item)
                                        setPostType(item.key as EHelpPostType)
                                      }}
                                      getOptionLabel={(option: DropDownElement) => option.value}
                                      isOptionEqualToValue={(option, value) => option.value === value.value}
                                      fullWidth
                                      size='small'
                                      disableClearable
                                      renderInput={(params) => (
                                        <TextFieldComponent
                                          {...params}
                                          label={row.label}
                                          required={row.required}
                                          name={row.id}
                                        />
                                      )}
                                    />
                                  )}
                                />}
                            </>
                          ) : (
                            <Controller
                              name='date'
                              defaultValue={date}
                              control={control}
                              render={
                                ({ field: { onChange } }) =>
                                  <>
                                    <DatePickerComponent
                                      label={row.label}
                                      value={date}
                                      size='small'
                                      minDate={new Date(Date.now())}
                                      onChangeDate={(event) => { onChange(event); event !== null && setDate(event) }}
                                      fullWidth
                                    />
                                    <div>{!!errors[row.id]}</div>
                                  </>
                              }
                            />
                          ))}
                      </Box>}
                  </React.Fragment>
                ))}
              </Grid>
              <Grid item xs={12} md={7} className={classes.imgBox}>
                <Stack width='100%'>
                  <Controller
                    control={control}
                    name='uploadedImageId'
                    render={({ field: { onChange } }) => (
                      <>
                        {getValues('type').key === EHelpPostType.YtVideo && (
                          <VideoComponent
                            src={data?.videoUrl}
                            type='YT_VIDEO'
                            height={450}
                            fallbackThumb='/src/shared/assets/images/icon-img-placeholder.svg'
                          />
                        )}
                        {getValues('type').key === EHelpPostType.Text && (
                          <MediaUploadComponent
                            file={data?.picture}
                            getMediaData={(params) => scanFile(params)}
                            setMediaFile={(file, fileId, type) => {
                              if (!file) setValue('uploadedImageId', undefined , { shouldDirty: true })
                            }}
                            loading={showImageLoader}
                            allowVideoUpload={false}
                            cropBoxResizable
                          />
                        )}
                        {getValues('type').key === EHelpPostType.Video && (
                          <MediaUploadComponent
                            file={data?.video?.videoUrl}
                            setMediaFile={(file: string | undefined) => {
                              setValue('videoUrl', file)
                              onChange(file)
                            }}
                            allowVideoUpload
                            isVideo
                          />
                        )}
                      </>
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
            <Box className={classes.button}>
              <PreloaderButton
                position='right'
                loading={(onCreateEditorHelpPost?.status !== 'failed' || onUpdateEditorHelpPost?.status !== 'failed') && (onCreateEditorHelpPost?.inProgress || onUpdateEditorHelpPost?.inProgress)}
                onClick={handleSubmit(handleUpdateEditorHelpPost)}
                disableButton={!isDirty}
                text={helpPostId ? t('coreHelp:saveChanges') : t('coreHelp:createHelpPost')}
              />
            </Box>
          </Grid>
        </form>
      </Paper>
    </Box>
  )
}

export default EditorHelpPostEditor
